import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Cancel,
  Payment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSubscriptionStatus, createSubscription, changeSubscription, cancelSubscription, uncancelSubscription, cancelScheduledChange, getAvailablePlans, previewSubscriptionChange, createCustomerPortalSession, restoreSubscription, getPricingPage } from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import BaseDialog from '../dialogs/BaseDialog';

interface Plan {
  id: number;
  name: string;
  price_monthly?: number;
  price_yearly?: number;
}

interface SubscriptionStatus {
  plan_type: number;
  billing_period: string;
  status: string;
  current_period_end: string | null;
  is_active: boolean;
  scheduled_plan_type?: number | null;
  scheduled_billing_period?: string | null;
}

interface ProrationDetail {
  description: string;
  amount: number;
}

interface ChangePreview {
  current_plan: string;
  new_plan: string;
  current_billing_period: string;
  new_billing_period: string;
  is_upgrade: boolean;
  immediate_amount: number;
  next_billing_date: string | null;
  proration_details: ProrationDetail[];
}

const SubscriptionSettings: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { refreshUser } = useUser();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUncancelDialog, setShowUncancelDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [preview, setPreview] = useState<ChangePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [restoreLoading, setRestoreLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansResponse, statusResponse, pricingResponse] = await Promise.all([
        getAvailablePlans(),
        getSubscriptionStatus(),
        getPricingPage(),
      ]);
      // 価格情報をマージ
      const pricingPlans = pricingResponse.data?.plans || [];
      const merged = plansResponse.data.map((p: Plan) => {
        const match = pricingPlans.find((pp: any) => pp.plan_type === p.id);
        return {
          ...p,
          price_monthly: match?.price_monthly,
          price_yearly: match?.price_yearly,
        };
      });
      setPlans(merged);
      setSubscription(statusResponse.data);
      if (statusResponse.data.billing_period) {
        setBillingPeriod(statusResponse.data.billing_period);
      }
    } catch (err: any) {
      setError(t('subscription.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: Plan, period: 'monthly' | 'yearly') => {
    setSelectedPlan(plan);
    setBillingPeriod(period);
    setPreview(null);
    setShowUpgradeDialog(true);

    // アクティブなサブスクがある場合のみプレビュー取得
    if (subscription?.is_active && subscription.status !== 'canceled') {
      try {
        setPreviewLoading(true);
        const res = await previewSubscriptionChange({ plan_type: plan.id, billing_period: period });
        setPreview(res.data);
      } catch {
        // プレビュー取得失敗しても変更は可能にする
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;

    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      // アクティブなサブスクがあればプラン変更、なければ新規作成
      if (subscription?.is_active && subscription.status !== 'canceled') {
        const response = await changeSubscription({ plan_type: selectedPlan.id, billing_period: billingPeriod });
        setSuccess(response.data.message || t('subscription.changeAccepted'));
        fetchData();
        refreshUser();
      } else {
        const response = await createSubscription({ plan_type: selectedPlan.id, billing_period: billingPeriod });
        // Stripeのチェックアウトページにリダイレクト
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('subscription.createFailed'));
    } finally {
      setActionLoading(false);
      setShowUpgradeDialog(false);
      setSelectedPlan(null);
    }
  };

  const handleUncancelSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);
      await uncancelSubscription();
      setSuccess(t('subscription.uncanceled'));
      setShowUncancelDialog(false);
      fetchData();
      refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.error || t('subscription.uncancelFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);
      setSuccess(null);

      await cancelSubscription();
      setSuccess(t('subscription.canceledMsg'));
      setShowCancelDialog(false);

      // データを再取得
      fetchData();
      refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.error || t('subscription.cancelSubFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const getCurrentPlan = () => {
    // 期限切れ/未加入は Free 扱い（plan_type に最後の購入プランが残っていても無視）
    if (!subscription || !subscription.is_active) return undefined;
    return plans.find(p => p.id === subscription.plan_type);
  };

  const getPlanStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'canceled':
        return 'error';
      case 'past_due':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPlanStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return t('subscription.statusActive');
      case 'canceled':
        return t('subscription.statusCanceled');
      case 'past_due':
        return t('subscription.statusPastDue');
      case 'inactive':
        return t('subscription.statusInactive');
      default:
        return status;
    }
  };

  const isSubscriptionExpired = () => {
    if (!subscription || !subscription.current_period_end) return false;
    return new Date() > new Date(subscription.current_period_end);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const currentPlan = getCurrentPlan();
  const isCrowdfunding = subscription?.plan_type === 9;
  const scheduledPlanName = subscription?.scheduled_plan_type != null
    ? (plans.find(p => p.id === subscription.scheduled_plan_type)?.name || '')
    : '';
  const scheduledPeriodLabel = subscription?.scheduled_billing_period === 'yearly'
    ? t('subscription.periodYearly')
    : subscription?.scheduled_billing_period === 'monthly'
    ? t('subscription.periodMonthly')
    : '';

  return (
    <Box>
      {/* 現在のプラン */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: subscription?.is_active ? 'primary.main' : 'divider',
          bgcolor: subscription?.is_active ? 'rgba(217,255,0,0.03)' : 'transparent',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {isCrowdfunding ? t('subscription.benefitPlan') : (currentPlan?.name || t('subscription.free'))}
            </Typography>
            {subscription && subscription.is_active && subscription.status !== 'inactive' && !isCrowdfunding && (
              <Chip
                label={getPlanStatusText(subscription.status)}
                color={getPlanStatusColor(subscription.status) as any}
                size="small"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
            {isCrowdfunding && (
              isSubscriptionExpired() ? (
                <Chip
                  label={t('subscription.expired')}
                  size="small"
                  color="warning"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                />
              ) : (
                <Chip
                  label={t('subscription.benefitActive')}
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(217,255,0,0.15)', color: 'primary.main', fontWeight: 700 }}
                />
              )
            )}
          </Box>
        </Box>

        {subscription?.is_active && subscription?.current_period_end && !isCrowdfunding && (
          <Typography variant="caption" color="text.secondary">
            {subscription.status === 'canceled' ? t('subscription.expiryDate') : t('subscription.nextRenewal')}:
            {' '}{new Date(subscription.current_period_end).toLocaleDateString('ja-JP')}
          </Typography>
        )}

        {subscription?.status === 'canceled' && !isSubscriptionExpired() && !isCrowdfunding && (
          <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.5 }}>
            {t('subscription.canceledNote')}
          </Typography>
        )}

        {subscription?.scheduled_plan_type != null && !isCrowdfunding && (
          <Box mt={0.75} display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="caption" color="info.main">
              {t('subscription.scheduledChange', { plan: scheduledPlanName, period: scheduledPeriodLabel })}
            </Typography>
            <Button
              size="small"
              sx={{ fontSize: '0.65rem', p: 0, minWidth: 0, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
              onClick={async () => {
                try {
                  setError(null);
                  setSuccess(null);
                  await cancelScheduledChange();
                  setSuccess(t('subscription.reservationCanceled'));
                  fetchData();
                  refreshUser();
                } catch (err: any) {
                  setError(err.response?.data?.error || t('subscription.cancelFailed'));
                }
              }}
            >
              {t('subscription.cancelReservation')}
            </Button>
          </Box>
        )}

        {isCrowdfunding && (
          <>
            {subscription?.current_period_end && (
              <Typography variant="caption" color="text.secondary" display="block">
                {isSubscriptionExpired()
                  ? t('subscription.benefitExpiredDate', { date: new Date(subscription.current_period_end).toLocaleDateString('ja-JP') })
                  : t('subscription.benefitExpiryDate', { date: new Date(subscription.current_period_end).toLocaleDateString('ja-JP') })}
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: isSubscriptionExpired() ? 'warning.main' : 'text.secondary' }}>
              {isSubscriptionExpired()
                ? t('subscription.benefitExpiredNote')
                : t('subscription.benefitActiveNote')}
            </Typography>
          </>
        )}

        {!isCrowdfunding && (
          <Box mt={1.5} display="flex" gap={2}>
            {/* 過去に課金歴があれば（解約済・期限切れ含む）Customer Portal は開ける */}
            {subscription && subscription.status !== 'inactive' && (
              <Button
                size="small"
                sx={{ fontSize: '0.7rem', p: 0, minWidth: 0, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                onClick={async () => {
                  try {
                    const res = await createCustomerPortalSession();
                    window.location.href = res.data.url;
                  } catch {
                    setError(t('subscription.portalFailed'));
                  }
                }}
              >
                {t('subscription.paymentHistory')}
              </Button>
            )}
            <Button
              size="small"
              disabled={restoreLoading}
              startIcon={restoreLoading ? <CircularProgress size={12} sx={{ color: 'text.secondary' }} /> : undefined}
              sx={{ fontSize: '0.7rem', p: 0, minWidth: 0, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
              onClick={async () => {
                try {
                  setRestoreLoading(true);
                  setError(null);
                  setSuccess(null);
                  const res = await restoreSubscription();
                  if (res.data.restored) {
                    setSuccess(res.data.message);
                    fetchData();
                    refreshUser();
                  } else {
                    setError(res.data.message);
                  }
                } catch (err: any) {
                  setError(err.response?.data?.error || t('subscription.restoreFailed'));
                } finally {
                  setRestoreLoading(false);
                }
              }}
            >
              {restoreLoading ? t('subscription.restoring') : t('subscription.restorePurchase')}
            </Button>
          </Box>
        )}
      </Box>

      {/* エラー・成功メッセージ（現在のプラン直下） */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* プラン一覧（クラファン特典ユーザーにはグレーアウト） */}
      <Box sx={{ opacity: isCrowdfunding ? 0.4 : 1, pointerEvents: isCrowdfunding ? 'none' : 'auto' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('subscription.plansHeading')}
        </Typography>
        <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
          {plans
            .filter(plan => plan.id > 0)
            .map((plan) => {
              const isCurrent = currentPlan?.id === plan.id;
              return (
                <Box
                  key={plan.id}
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isCurrent ? 'primary.main' : 'divider',
                    bgcolor: isCurrent ? 'rgba(217,255,0,0.03)' : 'transparent',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {plan.name}
                    </Typography>
                    {isCurrent && (
                      <Chip
                        label={t('subscription.current')}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: 'rgba(217,255,0,0.15)', color: 'primary.main',
                        }}
                      />
                    )}
                  </Box>
                  <Button
                    fullWidth
                    variant={isCurrent ? 'outlined' : 'contained'}
                    disabled={
                      (isCurrent && !subscription?.is_active) ||
                      (!isCurrent && subscription?.is_active === true && (subscription?.status === 'canceled' || subscription?.scheduled_plan_type != null))
                    }
                    onClick={() => {
                      if (isCurrent) {
                        setShowManageDialog(true);
                      } else if (!subscription?.is_active || subscription?.plan_type === 0) {
                        navigate('/pricing');
                      } else {
                        handleUpgrade(plan, subscription?.billing_period === 'yearly' ? 'yearly' : 'monthly');
                      }
                    }}
                    size="small"
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      ...(isCurrent
                        ? { borderColor: 'divider', color: 'text.secondary' }
                        : {}),
                    }}
                  >
                    {isCurrent
                      ? t('subscription.managePlan')
                      : (!subscription?.is_active || subscription?.plan_type === 0)
                        ? t('subscription.viewPricing')
                        : (plan.id > (subscription?.plan_type ?? 0) ? t('subscription.upgrade') : t('subscription.downgrade'))}
                  </Button>
                </Box>
              );
            })}
        </Box>
      </Box>

      {/* Stripe セキュリティバッジ */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        mt={3}
        mb={3}
        py={1.5}
        px={2}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(255,255,255,0.02)',
        }}
      >
        <Box
          component="svg"
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          sx={{ color: 'success.main', flexShrink: 0 }}
        >
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" fill="currentColor" opacity="0.2"/>
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M10 15.5l-3.5-3.5 1.41-1.41L10 12.67l5.59-5.59L17 8.5l-7 7z" fill="currentColor"/>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', letterSpacing: 0.3 }}>
          {t('subscription.stripeBadgePrefix')}<Box component="span" sx={{ color: 'text.primary' }}>Stripe</Box>{t('subscription.stripeBadgeSuffix')}
        </Typography>
      </Box>

      {/* プラン選択ダイアログ */}
      {(() => {
        const isChange = subscription?.is_active && subscription.status !== 'canceled';
        const isPeriodUp = !!(
          isChange && selectedPlan
          && selectedPlan.id === subscription?.plan_type
          && subscription?.billing_period === 'monthly'
          && billingPeriod === 'yearly'
        );
        const isPeriodDown = !!(
          isChange && selectedPlan
          && selectedPlan.id === subscription?.plan_type
          && subscription?.billing_period === 'yearly'
          && billingPeriod === 'monthly'
        );
        const isUpgrade = !!(
          isChange && selectedPlan
          && (selectedPlan.id > (subscription?.plan_type ?? 0) || isPeriodUp)
        );
        const isDowngrade = !!(
          isChange && selectedPlan
          && (selectedPlan.id < (subscription?.plan_type ?? 0) || isPeriodDown)
        );
        const dialogTitle = !isChange
          ? t('subscription.dialogSelectPlan')
          : isUpgrade
          ? t('subscription.dialogUpgrade')
          : isDowngrade
          ? t('subscription.dialogDowngrade')
          : t('subscription.dialogChange');
        const confirmLabel = !isChange
          ? t('subscription.confirmSelectPlan')
          : isUpgrade
          ? t('subscription.upgrade')
          : isDowngrade
          ? t('subscription.downgrade')
          : t('subscription.confirmChange');
        return (
          <BaseDialog
            open={showUpgradeDialog}
            onClose={() => setShowUpgradeDialog(false)}
            title={dialogTitle}
            maxWidth="sm"
            actions={
              <>
                <Button onClick={() => setShowUpgradeDialog(false)}>
                  {t('cancel', { ns: 'common' })}
                </Button>
                <Button
                  onClick={handleConfirmUpgrade}
                  variant="contained"
                  disabled={actionLoading || previewLoading}
                  startIcon={actionLoading ? <CircularProgress size={20} /> : <Payment />}
                >
                  {actionLoading ? t('subscription.processing') : confirmLabel}
                </Button>
              </>
            }
          >
            {selectedPlan && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('subscription.planSuffix', { name: selectedPlan.name })}
                </Typography>

                {!isChange && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('subscription.stripeRedirect')}
                  </Typography>
                )}

                {isChange && previewLoading && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {isChange && preview && (
                  <Box>
                    {isUpgrade ? (
                      <Box sx={{ mt: 1 }}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="text.secondary">{t('subscription.immediateCharge')}</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            ¥{preview.immediate_amount.toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('subscription.prorationNote')}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('subscription.afterPeriodNote')}
                      </Typography>
                    )}
                    {preview.next_billing_date && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('subscription.nextBilling', { date: new Date(preview.next_billing_date).toLocaleDateString('ja-JP') })}
                      </Typography>
                    )}
                  </Box>
                )}

                {isChange && !previewLoading && !preview && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {t('subscription.previewFailed')}
                  </Alert>
                )}
              </Box>
            )}
          </BaseDialog>
        );
      })()}

      {/* プラン管理ダイアログ（現在プランに対する操作集） */}
      <BaseDialog
        open={showManageDialog}
        onClose={() => setShowManageDialog(false)}
        title={t('subscription.manageTitle')}
        actions={
          <Button onClick={() => setShowManageDialog(false)}>{t('close', { ns: 'common' })}</Button>
        }
      >
        <Box display="flex" flexDirection="column" gap={1.25}>
          {/* キャンセル取り消し */}
          {subscription?.status === 'canceled' && !isSubscriptionExpired() && (
            <Button
              variant="contained"
              onClick={() => {
                setShowManageDialog(false);
                setShowUncancelDialog(true);
              }}
            >
              {t('subscription.uncancelAction')}
            </Button>
          )}

          {/* ダウングレード予約取消 */}
          {subscription?.scheduled_plan_type != null && subscription.status !== 'canceled' && (
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  setError(null);
                  setSuccess(null);
                  await cancelScheduledChange();
                  setSuccess(t('subscription.reservationCanceled'));
                  setShowManageDialog(false);
                  fetchData();
                  refreshUser();
                } catch (err: any) {
                  setError(err.response?.data?.error || t('subscription.cancelFailed'));
                }
              }}
            >
              {t('subscription.cancelScheduledAction')}
            </Button>
          )}

          {/* 期間切替 (canceled・scheduled以外) */}
          {subscription?.status !== 'canceled' && subscription?.scheduled_plan_type == null && currentPlan && (
            <Button
              variant="outlined"
              onClick={() => {
                setShowManageDialog(false);
                handleUpgrade(currentPlan, subscription?.billing_period === 'monthly' ? 'yearly' : 'monthly');
              }}
            >
              {subscription?.billing_period === 'monthly' ? t('subscription.switchToYearly') : t('subscription.switchToMonthly')}
            </Button>
          )}

          {/* 解約 (canceled以外) */}
          {subscription?.status !== 'canceled' && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setShowManageDialog(false);
                setShowCancelDialog(true);
              }}
            >
              {t('subscription.cancelPlan')}
            </Button>
          )}
        </Box>
      </BaseDialog>

      {/* キャンセル確認ダイアログ */}
      <BaseDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        title={t('subscription.cancelTitle')}
        actions={
          <>
            <Button onClick={() => setShowCancelDialog(false)}>
              {t('cancel', { ns: 'common' })}
            </Button>
            <Button
              onClick={handleCancelSubscription}
              variant="contained"
              color="error"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
            >
              {actionLoading ? t('subscription.processing') : t('subscription.cancelConfirmBtn')}
            </Button>
          </>
        }
      >
        <Typography variant="body1" paragraph>
          {t('subscription.cancelConfirmQuestion')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subscription.cancelConfirmNote')}
        </Typography>
      </BaseDialog>

      {/* キャンセル取消確認ダイアログ */}
      <BaseDialog
        open={showUncancelDialog}
        onClose={() => setShowUncancelDialog(false)}
        title={t('subscription.uncancelTitle')}
        actions={
          <>
            <Button onClick={() => setShowUncancelDialog(false)}>
              {t('cancel', { ns: 'common' })}
            </Button>
            <Button
              onClick={handleUncancelSubscription}
              variant="contained"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : undefined}
            >
              {actionLoading ? t('subscription.processing') : t('subscription.uncancelConfirmBtn')}
            </Button>
          </>
        }
      >
        <Typography variant="body1" paragraph>
          {t('subscription.uncancelConfirmQuestion')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subscription.uncancelConfirmNote')}
        </Typography>
      </BaseDialog>
    </Box>
  );
};

export default SubscriptionSettings; 